<template>
  <div class="start">
    <Loading
      v-if="appStatus === 'loading'"
      :userId="userId"
      :roomId="roomId"
      :token="token"
    />
    <Waiting
      v-if="appStatus === 'waiting' || appStatus === 'finished'"
      :userId="userId"
      :roomId="roomId"
      :token="token"
      :entityName="callInfo.entity.name"
      :logoUrl="callInfo.entity.logo"
      :role="callInfo.peer.type"
      :roomName="callInfo.room.description"
      :startTimestamp="callInfo.room.start"
      :endTimestamp="callInfo.room.finish"
      :roomFinishedBy="callInfo.room.finished_by"
      :isDirectJoiningEnabled="isDirectJoiningEnabled"
      :isRoomTimingsViewEnabled="isRoomTimingsViewEnabled"
      :isRoomEnded="isRoomEnded"
      :appStatus="appStatus"
      :connectionStatus="connectionStatus"
      :isCallInfoValid="isCallInfoValid"
      :getCallInfo="getCallInfo"
      :onStartCall="onStartCall"
      :showMessage="showSnackbar"
      :showMessagePermanent="showSnackbarPermanent"
    />
    <Switching
      v-if="appStatus === 'switching'"
      :userId="userId"
      :roomId="roomId"
      :token="token"
      :entityName="callInfo.entity.name"
      :logoUrl="callInfo.entity.logo"
      :role="callInfo.peer.type"
      :roomName="callInfo.room.description"
      :startTimestamp="callInfo.room.start"
      :endTimestamp="callInfo.room.finish"
      :roomStatus="callInfo.room.status_id"
      :isRoomTimingsViewEnabled="isRoomTimingsViewEnabled"
    />
    <VideoCall
      v-if="appStatus === 'calling'"
      :userId="userId"
      :roomId="roomId"
      :token="token"
      :role="callInfo.peer.type"
      :isChatEnabled="isChatEnabled"
      :isFileSharingEnabled="isFileSharingEnabled"
      :isDoctorDisconnectEnabled="isDoctorDisconnectEnabled"
      :isPatientEndRoomEnabled="isPatientEndRoomEnabled"
      :waitHeader="callInfo.entity.wait_msg_header"
      :waitText="callInfo.entity.wait_msg_text"
      :closeRoomCallback="onCloseCall"
      :expireRoomCallback="onExpireCall"
      :disconnectRoomCallback="onDisconnectCall"
      :switchDeviceCallback="onSwitchDevice"
      :showMessage="showSnackbar"
      :showMessageWithTimeout="showSnackbarWithTimeout"
      :showMessagePermanent="showSnackbarPermanent"
    />
    <v-snackbar v-model="snackbar" top :timeout="snackbarTimeout">
      {{ snackbarText }}
      <v-btn color="red" text @click="hideSnackbar">
        Cerrar
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Vue from 'vue';
import VueSocketIOExt from 'vue-socket.io-extended';
import io from 'socket.io-client';
import Loading from '@/components/Loading.vue';
import Waiting from '@/components/Waiting.vue';
import VideoCall from '@/components/VideoCall.vue';
import Switching from '@/components/Switching.vue';
import CallInfo from '@/services/CallInfo.js';

export default {
  name: 'Start',
  props: {
    userId: String,
    roomId: String,
    token: String
  },
  components: {
    Loading,
    Waiting,
    VideoCall,
    Switching
  },
  data: () => ({
    /*
      Posibles estados en los que se puede encontrar la aplicación
      - loading: Estado inicial previo a carga de propiedades de la sala.
      - waiting: Sala de espera y finalización. Necesita haber cargado las
                 propiedades de la sala previamente. Es el punto de entrada a la
                 video (calling).
      - calling: Proceso de video conferencia.
      - switching: Sala temporal de intercambio de dispositivo para facultativo.
      - finished: La sala está en estado finalizado (cierre, tiempo, etc).
    */
    appStatus: 'loading',
    /*
      Posibles estados de conexión del socket
      - pending: No se ha conectado previamente
      - connected: El socket se ha conectado correctamente
      - disconnected: Se ha desconectado el socket
    */
    connectionStatus: { current: 'pending', prev: 'pending' },
    // Guarda el id de usuario en la sesión del socket.
    // Básicamente se trabaja con este id en todas las acciones referentes a la
    // comunicación de sockets y webrtc en lugar de los que generamos en la api
    socketUserId: null,
    // Propiedades de la sesión leídas de la API
    callInfo: undefined,
    isChatEnabled: true,
    isFileSharingEnabled: true,
    isDoctorDisconnectEnabled: false,
    isPatientEndRoomEnabled: false,
    isDirectJoiningEnabled: false,
    isRoomTimingsViewEnabled: true,

    // Indica si se consideran válidas las propiedades de la sala leídas de  API
    isCallInfoValid: false,
    // Intervalo de tiempo para comprobar la información de la sesión
    callInfoChecker: undefined,
    // CSS personalizado
    style: undefined,
    // Propiedades de la barra de mensajes usada para notificar
    snackbar: false,
    snackbarText: undefined,
    snackbarDefaultTimeout: 6000,
    snackbarTimeout: -1
  }),
  async created() {
    // Insertamos un elemento style para el CSS personalizado que se insertará
    // directamente desde las propiedades de la sala en la API (getCallInfo)
    this.style = document.createElement('style');
    document.head.appendChild(this.style);
    // Intentamos obtener las propiedades de la sala inicialmente
    this.appStatus = 'loading';
    await this.getCallInfo();
    // Si la info es válida y la sala finalizó, no es necesario conectarnos
    if (this.isCallInfoValid && this.isRoomEnded) {
      this.appStatus = 'finished';
    } else {
      // Inicializamos el socket de comunicación con el servicio de señalización
      this.initializeSocket();
    }
  },
  computed: {
    isRoomEnded: function() {
      /*
        Posibles estados para las salas
        // Activas
        ROOM_PENDING: 1
        ROOM_RUNNING: 2
        // Finalizadas
        ROOM_FINISHED: 3
        ROOM_HALTED: 4
        ROOM_DESERTED: 5
        ROOM_CANCELED: 6
      */
      return this.callInfo.room.status_id > 2;
    }
  },
  methods: {
    // Crea una tarea a intervalos para obtener las propiedades de la sala
    setCallInfoChecker() {
      if (!this.callInfoChecker) {
        this.callInfoChecker = setInterval(async () => {
          await this.getCallInfo();
        }, 2000);
      }
    },
    // Obtiene las propiedades de la sala (entidad, sala, participante, sala,
    // servidores, etc) directamente desde la API
    async getCallInfo() {
      console.log(`Start: Obteniendo información de la sala '${this.roomId}'`);
      const response = await CallInfo.getCallInfo(
        this.userId,
        this.roomId,
        this.token
      );
      if (response) {
        this.callInfo = response.data;
        this.isChatEnabled = Boolean(this.callInfo.entity.chat_enabled);
        this.isFileSharingEnabled = Boolean(
          this.callInfo.entity.file_sharing_enabled
        );
        this.isDoctorDisconnectEnabled = Boolean(
          this.callInfo.entity.doctor_disconnect_enabled
        );
        this.isPatientEndRoomEnabled = Boolean(
          this.callInfo.entity.patient_end_room_enabled
        );
        this.isDirectJoiningEnabled = Boolean(
          this.callInfo.entity.direct_joining_enabled
        );
        this.isRoomTimingsViewEnabled = Boolean(
          this.callInfo.entity.timings_view_enabled
        );
        localStorage.signallingServer = this.callInfo.services.signalling;
        localStorage.turnCredentials = this.callInfo.services.turn_credentials;
        this.style.textContent = window.atob(this.callInfo.entity.style);
        // Quitamos el comprobador ya que ha terminado su tarea
        clearInterval(this.callInfoChecker);
        this.callInfoChecker = undefined;
        this.isCallInfoValid = true;

        // Si la app estaba cargando, la pasamos a la sala de espera porque ya
        // tenemos la info necesaria para renderizarla
        if (this.appStatus === 'loading') {
          this.appStatus = 'waiting';
        }
        console.log(
          `Start: Información de la sala '${this.roomId}' obtenida correctamente`
        );
      } else {
        console.log(
          `Start: Error no se pudo leer la información de la sala '${this.roomId}'`
        );
        this.isCallInfoValid = false;
      }
    },
    // Inicializa el socket de comunicación con el servidor de señalización y
    // establece los manejadores de eventos de comunicación
    initializeSocket() {
      console.log('Start: Inicializando socket');

      // const socket = io(`192.168.1.38:3000/imworld`);
      const socket = io(`${localStorage.signallingServer}/imworld`);
      Vue.use(VueSocketIOExt, socket);

      // Manejador para el evento de conexión
      socket.on('connect', async () => {
        // El id llega con el namespace delante /imworld#, lo quitamos
        const socketUserId = socket.id.slice(9);

        if (this.socketUserId && this.socketUserId != socketUserId) {
          console.log(
            `Start: Socket UserId cambió de ${this.socketUserId} a ${socketUserId}`
          );
        }
        this.socketUserId = socketUserId;
        console.log(
          `Start: Socket conectado. Socket UserId ${this.socketUserId}`
        );

        // Recuperación de conexión
        if (this.connectionStatus.current === 'disconnected') {
          console.log(
            `Start: Se restauró la conexión en el Socket. Socket UserId ${this.socketUserId}.`
          );
          this.showSnackbar(this.$t('message.start.connectionRecovered'));
        }
        // Actualizamos los datos de la sala desde API al recuperar conexión
        if (!this.callInfoChecker && !this.isCallInfoValid) {
          // Creamos una tarea a intervalos para obtener las propiedades bien
          this.setCallInfoChecker();
        }
        this.updateConnectionStatus('connected');
      });

      // Manejador para el evento de desconexión
      socket.on('disconnect', async reason => {
        console.log(
          `Start: Socket desconectado (${reason}). Socket UserId ${this.socketUserId}`
        );
        if (this.connectionStatus.current === 'connected') {
          if (reason === 'io server disconnect') {
            // La sesión finalizó y el server cerró el socket unilateralmente
            await this.getCallInfo();
            this.showSnackbarPermanent(this.$t('message.start.roomEnded'));
          } else {
            // La sesión no finalizó, se perdió la conexión
            this.showSnackbarPermanent(this.$t('message.start.connectionLost'));
          }
        }
        // Al desconectar, siempre vamos a la sala de espera, excepto si aun no
        // hemos pasado por la sala de espera anteriormente.
        if (this.appStatus !== 'loading') {
          this.appStatus = 'waiting';
        }
        // Al desconectar, invalidamos los datos de la sala para forzar que se
        // actualicen.
        clearInterval(this.callInfoChecker);
        this.callInfoChecker = undefined;
        this.isCallInfoValid = false;
        // Informamos a la ventana padre de que se ha producido una desconexión
        window.parent.postMessage(
          {
            func: 'changeState',
            state: 'disconnected'
          },
          '*'
        );
        this.updateConnectionStatus('disconnected');
      });

      // Manejador para el evento de error
      socket.on('connect_error', error => {
        console.log(`Start: Socket UserId ${this.socketUserId} ${error}`);
        // Se produjo un error en socket previo a alguna conexión
        if (this.connectionStatus.current === 'pending') {
          this.showSnackbarPermanent(
            this.$t('message.start.connectionDisconected')
          );
          clearInterval(this.callInfoChecker);
          this.callInfoChecker = undefined;
          this.isCallInfoValid = false;
          // Informamos a la ventana padre de que se ha producido una desconexión
          window.parent.postMessage(
            {
              func: 'changeState',
              state: 'disconnected'
            },
            '*'
          );
          this.updateConnectionStatus('disconnected');
        }
        // CHECKME: Y SI NO ES ANTES DE CONECTAR??
        // CHECKME: PUEDEN HABER ERRORES SIN HABER DESCONEXIONES??
      });
    },
    // Se pulsó el botón "Unirse"
    onStartCall() {
      this.appStatus = 'calling';
      window.parent.postMessage(
        {
          func: 'changeState',
          state: 'connected'
        },
        '*'
      );
    },
    // Se cerró la sala desde los controles de la sala de vídeo ("closed")
    onCloseCall(reason, isCallEnded) {
      this.showSnackbar(reason);
      if (isCallEnded) {
        this.appStatus = 'finished';
      } else {
        this.appStatus = 'waiting';
      }
      window.parent.postMessage(
        {
          func: 'changeState',
          state: 'closed'
        },
        '*'
      );
    },
    // Se recibió el evento de expiración de sesión (cierre forzoso "halted")
    onExpireCall(reason) {
      this.showSnackbar(reason);
      this.appStatus = 'finished';
      window.parent.postMessage(
        {
          func: 'changeState',
          state: 'halted'
        },
        '*'
      );
    },
    // El facultativo solicitó cambio de dispositivo desde la sala de vídeo
    onSwitchDevice(reason) {
      this.showSnackbar(reason);
      this.appStatus = 'switching';
      window.parent.postMessage(
        {
          func: 'changeState',
          state: 'disconnected'
        },
        '*'
      );
    },
    // Se detectó una desconexión en la sala de vídeo
    // CHECKME: REALMENTE NECESITAMOS ESTA CALLBACK?? NO SERÍA MÁS ADECUADO
    // HACERLO NOSOTROS EN EL EVENTO DEL SOCKET DISCONNECT??
    onDisconnectCall(reason) {
      console.log(`En disconnect ${reason}`);
      /*
      this.showSnackbar(reason);
      this.appStatus = 'waiting';
      window.parent.postMessage(
        {
          func: 'changeState',
          state: 'disconnected'
        },
        '*'
      );
      */
    },
    // Métodos para gestionar la barra de mensajes (Snackbar)
    showSnackbar(msg) {
      this.snackbar = true;
      this.snackbarText = msg;
      this.snackbarTimeout = this.snackbarDefaultTimeout;
    },
    hideSnackbar() {
      this.snackbar = false;
      this.snackbarText = undefined;
    },
    showSnackbarPermanent(msg) {
      this.snackbar = true;
      this.snackbarText = msg;
      this.snackbarTimeout = 0;
    },
    showSnackbarWithTimeout(msg, timeout) {
      this.snackbar = true;
      this.snackbarText = msg;
      this.snackbarTimeout = timeout;
    },
    updateConnectionStatus(status) {
      this.connectionStatus.prev = this.connectionStatus.current;
      this.connectionStatus.current = status;
    }
  }
};
</script>

<style></style>
